// TraceFull v7 — AI Control Plane
import { useState, useRef, useEffect } from 'react';
import { T, SectionHeader, Chip, Card } from '../components/ui';
import type { Alert, AgentState } from '../api/client';

interface PageProps {
  alerts:        Alert[];
  agents:        AgentState[];
  heartbeat:     { agentsActive: number; openAlerts: number; uptime: number } | null;
  refetchAlerts: () => void;
  refetchAgents: () => void;
  setAlerts:     (fn: (prev: Alert[]) => Alert[]) => void;
}

interface Message { id: string; role: 'user' | 'assistant'; text: string; ts: string; agentType?: string; }

const AGENT_META: Record<string, { icon: string; color: string; label: string }> = {
  alert:       { icon: '🔴', color: T.crimson,  label: 'Alert'        },
  provenance:  { icon: '🌿', color: T.sage,     label: 'Provenance'   },
  procurement: { icon: '💼', color: '#d4a840',  label: 'Procurement'  },
  supply:      { icon: '🔗', color: T.sapphire, label: 'Supply Chain' },
  legal:       { icon: '⚖️',  color: '#a898d8',  label: 'Legal'        },
  finance:     { icon: '💰', color: T.copper,   label: 'Finance'      },
};

const CANNED_RESPONSES: Record<string, string[]> = {
  alert: [
    "Scanning alert queue… Found 3 open alerts. The highest severity is CRITICAL: Andean Harvest lot Q-2025-H2 aflatoxin levels at 28ppb, exceeding the FDA 20ppb threshold. I recommend immediate quarantine of affected lot and downstream notification to all retailersApi.",
    "Running compliance check… 2 products from Andean Harvest Co. are flagged HIGH. Supply chain disruption risk is ELEVATED. Notifying Procurement and Legal agents via A2A messaging now.",
  ],
  provenance: [
    "Provenance analysis complete. Andean Harvest Co. has a current score of 72 — MEDIUM risk. Their last 3 certifications are valid, but lot-level harvest date documentation is missing for 4 of 14 SKUs. Recommend requesting documentation within 48h before next bid evaluation.",
    "Blockchain anchoring status: 847 products have on-chain verification. 12 products are pending anchor confirmation (block finality ~3 min). All 2024 harvest records are immutably stored.",
  ],
  procurement: [
    "Budget utilization: $47,200 of $50,000 monthly cap consumed (94%). 2 bids pending approval: Village Farms ($22,400 — recommend ACCEPT, score 91), Pacific Seafood ($61,500 — recommend ESCALATE, score 58 below threshold). Available budget: $2,800.",
    "Analyzing bid #B002 from Village Farms International. Price is 4% below market average, provenance score 91 (excellent), certifications: USDA Organic + GlobalG.A.P. + Rainforest Alliance. Delivery: 5 days. My recommendation: ACCEPT immediately.",
  ],
  supply: [
    "Supply chain network analysis: 847 active products across 6 retailersApi. 3 suppliers are HIGH risk this week — Andean Harvest Co., Pacific Seafood LLC, and Fresherized Foods. Cold chain integrity: 98.2% (2 IoT violations in past 7 days, both minor temp excursions).",
    "Disruption probability for Q1 2025: 12% elevated risk due to Peruvian harvest conditions. Recommend diversifying Tier 1 quinoa supply to a second approved supplier. I've identified 3 candidates with scores above 80.",
  ],
  legal: [
    "FSMA compliance status: 94% of products have complete documentation. 6 products are missing Foreign Supplier Verification Program records — these are flagged for review. EUDR compliance for cocoa/coffee: 87% — 3 Tier 2 suppliers still require origin documentation for Côte d'Ivoire sourcing.",
    "Reviewing contract with Andean Harvest Co. re: aflatoxin liability clause. Section 8.3 allows rejection without penalty when FDA thresholds are exceeded. You are within your rights to reject lot Q-2025-H2 and claim a full refund under clause 8.3(b).",
  ],
  finance: [
    "Spend analysis for the trailing 30 days: Total procurement spend $214,800. By category: Produce 42%, Protein 31%, Grains 18%, Dairy/Other 9%. Budget variance: +2.4% over plan. Top cost driver: protein prices up 8% YoY. Savings opportunities identified: $18,400 through supplier consolidation.",
    "Cash flow impact of current recall: Estimated $42,000 in affected inventory at retail cost. Insurance coverage under policy #TF-2024-SCR should cover 80% ($33,600). Net exposure: ~$8,400. Recommend filing claim within 72 hours.",
  ],
};

function getResponse(input: string, agentType: string): string {
  const responses = CANNED_RESPONSES[agentType] || CANNED_RESPONSES.alert;
  const idx = Math.floor(Math.random() * responses.length);
  return responses[idx];
}

function ts(): string {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function ControlPage({ agents, heartbeat }: PageProps) {
  const [messages, setMessages]   = useState<Message[]>([
    { id: '0', role: 'assistant', text: 'Control Plane active. All 6 agents operational. Select an agent and send a message to interact directly, or use the system controls below.', ts: ts(), agentType: 'system' },
  ]);
  const [input, setInput]         = useState('');
  const [activeAgent, setAgent]   = useState('provenance');
  const [loading, setLoading]     = useState(false);
  const bottomRef                 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg: Message = { id: Date.now().toString(), role: 'user', text: input.trim(), ts: ts() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Simulate agent processing delay
    setTimeout(() => {
      const reply: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: getResponse(input, activeAgent),
        ts: ts(),
        agentType: activeAgent,
      };
      setMessages(prev => [...prev, reply]);
      setLoading(false);
    }, 900 + Math.random() * 800);
  };

  const quickTrigger = (prompt: string) => { setInput(prompt); };

  const QUICK_PROMPTS = [
    { label: 'Run compliance check', prompt: 'Run a full compliance check across all products and flag any issues.' },
    { label: 'Evaluate open bids',   prompt: 'Evaluate all pending procurement bids and give me your recommendations.' },
    { label: 'Summarize alerts',     prompt: 'Summarize the current open alerts and prioritize by business impact.' },
    { label: 'Budget status',        prompt: 'What is the current procurement budget utilization and forecast?' },
    { label: 'Chain risk scan',      prompt: 'Scan the supply chain for disruption risks in the next 30 days.' },
    { label: 'EUDR status',          prompt: 'What is our current EUDR compliance status for regulated products?' },
  ];

  return (
    <div className="fade-up" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      <SectionHeader
        title="AI Control Plane"
        sub="Direct interface to all 6 AI agents — query, trigger, and monitor in real time"
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.sage, animation: 'pulse 2s infinite' }} />
            <span style={{ fontSize: 12, color: T.sub }}>{heartbeat?.agentsActive || 6} agents active</span>
          </div>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 18, flex: 1, minHeight: 0 }}>

        {/* Left: agent selector + system status */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card style={{ padding: '12px' }}>
            <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Active Agent</div>
            {Object.entries(AGENT_META).map(([key, m]) => {
              const agentState = agents.find(a => a.agent_type === key);
              const statusC = agentState?.status === 'active' ? T.sage : agentState?.status === 'alert' ? T.crimson : T.amber;
              return (
                <div key={key} onClick={() => setAgent(key)} style={{
                  display: 'flex', gap: 10, alignItems: 'center', padding: '9px 10px',
                  borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                  background: activeAgent === key ? m.color + '18' : 'transparent',
                  border: `1px solid ${activeAgent === key ? m.color + '50' : 'transparent'}`,
                  transition: 'all .1s',
                }}>
                  <span style={{ fontSize: 16 }}>{m.icon}</span>
                  <span style={{ fontSize: 12, fontWeight: activeAgent === key ? 700 : 400, color: activeAgent === key ? m.color : T.sub, flex: 1 }}>{m.label}</span>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusC, flexShrink: 0 }} />
                </div>
              );
            })}
          </Card>

          <Card style={{ padding: '12px' }}>
            <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Quick Triggers</div>
            {QUICK_PROMPTS.map(q => (
              <button key={q.label} onClick={() => quickTrigger(q.prompt)} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 7,
                color: T.sub, fontSize: 11, cursor: 'pointer', marginBottom: 5,
                transition: 'all .1s',
              }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = T.copper, e.currentTarget.style.color = T.copper)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = T.border, e.currentTarget.style.color = T.sub)}
              >{q.label}</button>
            ))}
          </Card>

          <Card style={{ padding: '12px' }}>
            <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>System Status</div>
            {[
              { label: 'Open Alerts',    value: heartbeat?.openAlerts || 3,  color: T.crimson },
              { label: 'Agents Active',  value: heartbeat?.agentsActive || 6, color: T.sage   },
              { label: 'Uptime',         value: `${heartbeat?.uptime || 99.9}%`, color: T.copper },
            ].map(s => (
              <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
                <span style={{ color: T.sub }}>{s.label}</span>
                <span style={{ color: s.color, fontWeight: 700 }}>{s.value}</span>
              </div>
            ))}
          </Card>
        </div>

        {/* Right: chat interface */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Agent banner */}
          <div style={{
            background: AGENT_META[activeAgent].color + '18',
            border: `1px solid ${AGENT_META[activeAgent].color}40`,
            borderRadius: '10px 10px 0 0', padding: '10px 16px',
            display: 'flex', gap: 10, alignItems: 'center',
          }}>
            <span style={{ fontSize: 18 }}>{AGENT_META[activeAgent].icon}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: AGENT_META[activeAgent].color }}>
              {AGENT_META[activeAgent].label} Agent
            </span>
            <Chip label="v7 Phase 5" color={T.copper} />
            <Chip label="Sonnet 4.6" color={T.sapphire} />
            <span style={{ marginLeft: 'auto', fontSize: 11, color: T.sub }}>Memory + Structured Output active</span>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px',
            background: T.panel, border: `1px solid ${T.border}`, borderTop: 'none',
          }}>
            {messages.map(m => (
              <div key={m.id} style={{
                display: 'flex', gap: 12, marginBottom: 18,
                flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
              }}>
                {/* Avatar */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: m.role === 'user' ? T.copper + '30' : (AGENT_META[m.agentType || activeAgent]?.color || T.sage) + '30',
                  border: `1px solid ${m.role === 'user' ? T.copper : (AGENT_META[m.agentType || activeAgent]?.color || T.sage)}50`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                }}>
                  {m.role === 'user' ? '👤' : (AGENT_META[m.agentType || activeAgent]?.icon || '🤖')}
                </div>

                <div style={{ maxWidth: '78%' }}>
                  <div style={{
                    background: m.role === 'user' ? T.copper + '18' : T.bg,
                    border: `1px solid ${m.role === 'user' ? T.copper + '40' : T.border}`,
                    borderRadius: m.role === 'user' ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                    padding: '10px 14px', fontSize: 13, color: T.text, lineHeight: 1.65,
                  }}>
                    {m.text}
                  </div>
                  <div style={{ fontSize: 10, color: T.dim, marginTop: 4, textAlign: m.role === 'user' ? 'right' : 'left' }}>{m.ts}</div>
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: AGENT_META[activeAgent].color + '30', border: `1px solid ${AGENT_META[activeAgent].color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                  {AGENT_META[activeAgent].icon}
                </div>
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: '4px 12px 12px 12px', padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {[0, 0.2, 0.4].map(d => (
                      <div key={d} style={{ width: 7, height: 7, borderRadius: '50%', background: AGENT_META[activeAgent].color, animation: `pulse 1.4s ${d}s infinite` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input bar */}
          <div style={{
            display: 'flex', gap: 10, padding: '12px',
            background: T.bg, border: `1px solid ${T.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px',
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder={`Ask the ${AGENT_META[activeAgent].label} Agent anything…`}
              style={{
                flex: 1, background: T.panel, border: `1px solid ${T.border}`, borderRadius: 8,
                padding: '10px 14px', color: T.text, fontSize: 13, outline: 'none',
                fontFamily: "'DM Sans', sans-serif",
              }}
            />
            <button onClick={send} disabled={!input.trim() || loading} style={{
              padding: '10px 20px', background: AGENT_META[activeAgent].color + '22',
              border: `1px solid ${AGENT_META[activeAgent].color}`,
              color: AGENT_META[activeAgent].color, borderRadius: 8, fontSize: 13,
              fontWeight: 700, cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
              opacity: input.trim() && !loading ? 1 : 0.4, transition: 'opacity .15s',
            }}>Send ↵</button>
          </div>
        </div>
      </div>
    </div>
  );
}
